import React from "react";
import { useTranslation } from "react-i18next";
import Checkbox from "components/form/checkbox/Checkbox";
import { MAX_COLUMNS } from "../../domain/column-presets";
import { findRow } from "../../domain/find";
import { selectDocument } from "../../domain/selectors";
import { useEditorActions, useEditorStore } from "../../domain/redux/hooks";

type ColumnCountFieldProps = {
  rowId: string;
};

export function ColumnCountField({ rowId }: ColumnCountFieldProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { addColumn, removeColumn } = useEditorActions();
  const row = findRow(document, rowId);
  if (!row) {
    return null;
  }

  const columnCount = row.columns.length;

  function setColumnCount(targetCount: number) {
    if (targetCount < 1 || targetCount > MAX_COLUMNS || targetCount === columnCount) {
      return;
    }

    if (targetCount > columnCount) {
      for (let nextCount = columnCount; nextCount < targetCount; nextCount += 1) {
        addColumn(rowId);
      }
      return;
    }

    const columnIdsToRemove = row.columns.slice(targetCount).map(column => column.id);
    columnIdsToRemove.forEach(columnId => {
      removeColumn(rowId, columnId);
    });
  }

  return (
    <div className="email-builder-settings-column-count">
      <p className="email-builder-settings-column-count__label">
        {t("How many columns would you like?")}
      </p>
      <div
        className="email-builder-settings-column-count__options"
        role="radiogroup"
        aria-label={t("Column count")}
      >
        {Array.from({ length: MAX_COLUMNS }, (_, index) => {
          const value = index + 1;
          const selected = value === columnCount;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              data-selected={selected ? "true" : "false"}
              className="email-builder-settings-column-count__option"
              onClick={() => setColumnCount(value)}
            >
              <Checkbox circle size="small" checked={selected} readOnly labelHidden />
              <span className="email-builder-settings-column-count__option-label">{value}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
